import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { GroupContainer } from "../../../../common/components";
import { ShowItemsCompact } from "../../../common/controls";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionDocumentationView as SnapshotEditionDocumentationView } from "./EditionDocumentationView";
import { EditionContractPublisherView as SnapshotEditionContractPublisherView } from "./EditionContractPublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { MusicalWorkView as SnapshotMusicalWorkView } from "./MusicalWorkView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { FormControl } from "@mui/material";

type PublisherViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const PublisherView: React.FC<PublisherViewProps> = ({
  snapshotData,
  snapshotEntityName,
  snapshotRecordId,
}) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop("Publisher");
  const renderExtension = useSnapshotExtension("Publisher");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "EditionDocumentation":
          return (
            <SnapshotEditionDocumentationView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionContractPublisher":
          return (
            <SnapshotEditionContractPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWork":
          return (
            <SnapshotMusicalWorkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <GroupContainer
        id="basicInformation"
        title={langStore.groupPublisherBasicInformationLabel}
      >
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.PublisherPersonTypeLabel}
            value={String(snapshotData?.personType ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherNameLabel}
            value={String(snapshotData?.name ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherPseudonymLabel}
            value={String(snapshotData?.pseudonym ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherTaxIdLabel}
            value={String(snapshotData?.taxId ?? "")}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="legalInformation"
        title={langStore.groupPublisherLegalInformationLabel}
      >
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.PublisherCaeIpiLabel}
            value={String(snapshotData?.caeIpi ?? "")}
          />
          <ShowItemsCompact
            label={langStore.PublisherRepresentativesLabel}
            showLabel={true}
            enableDownload={true}
            entityName="PublisherRepresentative"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              {
                name: "name",
                title: langStore.PublisherRepresentativeNameLabel,
              },
              {
                name: "role",
                title: langStore.PublisherRepresentativeRoleLabel,
              },
              {
                name: "email",
                title: langStore.PublisherRepresentativeEmailLabel,
              },
              {
                name: "isSigner",
                title: langStore.PublisherRepresentativeIsSignerLabel,
              },
            ]}
            rows={(snapshotData?.representatives ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                name: __r?.name ?? "",
                role: __r?.role ?? "",
                email: __r?.email ?? "",
                isSigner: __r?.isSigner ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal(
                "PublisherRepresentative",
                (snapshotData?.representatives ?? [])[idx]
              );
            }}
          />
        </FormControl>
      </GroupContainer>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
    </>
  );
};
